import type { Router } from 'vue-router'

import * as Sentry from '@sentry/vue'

export const RELOAD_GUARD_KEY = 'stale-chunk-reload-at'
export const RELOAD_WINDOW_MS = 60_000
export const RELOAD_PENDING_MS = 5_000
export const RELOAD_RECOVERY_MS = 15_000

export const DYNAMIC_IMPORT_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i

// A lazy route that fails to load never reaches afterEach, so this is the only record of its URL.
let pendingTarget: string | null = null

// True from deciding to reload until the reload; the dying page's follow-on errors are noise.
let reloadPending = false
let reloadPendingTimer: ReturnType<typeof setTimeout> | undefined

export function isStaleChunkReloadPending(): boolean {
  return reloadPending
}

// The target comes from the URL, so it must stay on this origin; anything else falls back to a plain reload.
function sameOriginTarget(router: Router, target: string): string | null {
  try {
    const url = new URL(router.resolve(target).href, window.location.href)
    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null
  } catch {
    return null
  }
}

function tryReload(router: Router, target?: string): boolean {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY))
    // The timestamp is what stops a permanently broken chunk from reloading forever.
    if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_WINDOW_MS) return false
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Without the guard a reload could loop, so storage failures mean no reload.
    return false
  }

  reloadPending = true
  clearTimeout(reloadPendingTimer)
  reloadPendingTimer = setTimeout(() => {
    reloadPending = false
  }, RELOAD_PENDING_MS)

  const href = target ? sameOriginTarget(router, target) : null

  Sentry.addBreadcrumb({
    category: 'stale-chunk',
    message: `Reloading after a failed dynamic import${href ? ` (target: ${href})` : ''}`,
    level: 'info',
  })

  if (href) window.location.assign(href)
  else window.location.reload()
  return true
}

// The old page's breadcrumb and events are lost, so recoveries are counted by the page after it.
function reportRecovery(): void {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY))
    if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_RECOVERY_MS) {
      Sentry.metrics.count('stale_chunk.reload_recovered', 1)
    }
  } catch {
    // No storage, no guard, nothing to recover from.
  }
}

export function installStaleChunkReload(router: Router): void {
  router.beforeEach((to) => {
    pendingTarget = to.fullPath
  })
  // A superseded navigation finishes after the newer one set its target, so only clear its own.
  router.afterEach((to) => {
    if (to.fullPath === pendingTarget) pendingTarget = null
  })

  window.addEventListener('vite:preloadError', (event) => {
    if (tryReload(router, pendingTarget ?? undefined)) event.preventDefault()
  })

  // Covers bare import() routes, which skip Vite's preload helper.
  router.onError((error, to) => {
    pendingTarget = null
    if (DYNAMIC_IMPORT_ERROR.test(String(error?.message ?? error))) tryReload(router, to.fullPath)
  })

  reportRecovery()
}
